import { useState } from "react";
import { IconChecks, IconX } from "@tabler/icons-react";
import {
  Button,
  DatePickerInput,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Group,
  Stack,
  Text,
} from "@/components/ui";
import type { BandejaAutorizacionItem } from "@/lib/types";
import { parseIso, formatIso } from "@/features/datos-generales/utils/iso-date";
import { toastExito, toastAdvertencia } from "@/lib/toast";
import { useAutorizarLote } from "../hooks/use-bandeja-autorizacion";

/**
 * Barra de acción MASIVA de la bandeja · aparece cuando hay solicitudes
 * seleccionadas y permite autorizarlas todas de una vez (cualquier autorizador).
 * Si alguna está en su ÚLTIMO nivel, el modal pide una fecha de pago (P26) que se
 * aplica a esas; el resto la ignora. Muestra un resumen de autorizadas/fallidas.
 */
export function BandejaAccionMasiva({
  seleccionadas,
  onLimpiar,
  onAutorizado,
}: {
  seleccionadas: BandejaAutorizacionItem[];
  onLimpiar: () => void;
  onAutorizado: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [fechaPago, setFechaPago] = useState("");

  const autorizar = useAutorizarLote({
    onExito: (r) => {
      setAbierto(false);
      setFechaPago("");
      if (r.fallidas === 0) {
        toastExito({
          title: "Solicitudes autorizadas",
          message: `${r.autorizadas} solicitud(es) autorizada(s).`,
        });
      } else {
        const primera = r.resultados.find((x) => !x.ok);
        toastAdvertencia({
          title: `${r.autorizadas} autorizada(s) · ${r.fallidas} con error`,
          message: primera
            ? `Ej.: #${primera.numeroSolicitud} · ${primera.errorMensaje ?? primera.errorCodigo}`
            : "Algunas solicitudes no se pudieron autorizar.",
        });
      }
      onAutorizado();
    },
  });

  const n = seleccionadas.length;
  if (n === 0) return null;

  // Fecha de pago obligatoria si alguna seleccionada está en su último nivel.
  const requiereFechaPago = seleccionadas.some((s) => s.nivelActual === s.cantidadNiveles);
  const ids = seleccionadas.map((s) => s.id);

  return (
    <>
      <Group
        justify="space-between"
        wrap="nowrap"
        className="rounded-lg border border-multiva-300 bg-multiva-50 px-3 py-2 dark:border-multiva-800 dark:bg-multiva-900/20"
      >
        <Text size="sm" fw={600}>
          {n} solicitud{n === 1 ? "" : "es"} seleccionada{n === 1 ? "" : "s"}
        </Text>
        <Group gap="xs" wrap="nowrap">
          <Button variant="subtle" leftSection={<IconX size={14} />} onClick={onLimpiar}>
            Limpiar
          </Button>
          <Button
            className="bg-exito-600 hover:bg-exito-700"
            leftSection={<IconChecks size={14} />}
            onClick={() => setAbierto(true)}
          >
            Autorizar seleccionadas
          </Button>
        </Group>
      </Group>

      <Dialog open={abierto} onOpenChange={(o) => !o && !autorizar.isPending && setAbierto(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Autorizar {n} solicitud{n === 1 ? "" : "es"}</DialogTitle>
          </DialogHeader>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Se autorizará el nivel actual de {n} solicitud{n === 1 ? "" : "es"}. Las que estén en su
              último nivel completarán el flujo de autorización.
            </Text>
            {requiereFechaPago && (
              <>
                <Text size="sm">
                  Al menos una está en su <strong>último nivel</strong> · establece la fecha de pago
                  (se aplicará a esas solicitudes).
                </Text>
                <DatePickerInput
                  label="Fecha de pago"
                  placeholder="Selecciona la fecha…"
                  required
                  minDate={new Date()}
                  value={parseIso(fechaPago)}
                  onChange={(d) => setFechaPago(formatIso(d))}
                />
              </>
            )}
          </Stack>
          <DialogFooter>
            <Button variant="default" onClick={() => setAbierto(false)} disabled={autorizar.isPending}>
              Cancelar
            </Button>
            <Button
              className="bg-exito-600 hover:bg-exito-700"
              leftSection={<IconChecks size={14} />}
              disabled={requiereFechaPago && !fechaPago}
              loading={autorizar.isPending}
              onClick={() => autorizar.mutate({ ids, fechaPago: requiereFechaPago ? fechaPago : null })}
            >
              Autorizar {n}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
